<template>
  <div class="question-picker-wrapper">
    <v-select 
      v-if="questions != null" 
      v-model="selected" 
      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
      label="description" 
      :options="questions" 
      class="select-size-lg question-picker-select" 
      :reduce="question => question"
      :placeholder="$t('survey.surveyquestion.select_question')"
      :clearable="false"
      @input="onSelectionChanged">
      <template #option="{ description, id }">
        <div class="d-flex align-items-center question-option">
          <span v-html="description"></span>
        </div>
      </template>
      <template #selected-option="{ description }">
        <div class="d-flex align-items-center selected-option-content">
          <span class="text-truncate" v-html="description"></span>
        </div>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import store from '@/store'
import userStoreModule from '../surveyquestionStoreModule'

export default {
  components: {
    vSelect,
  },

  props: {
    initialQuestion: {
      type: [Object, Number, String],
      default: null,
    },
    surveyInfo: {
      type: Object,
      default: null,
    },
    surveyId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      questions: null,
      selected: null,
      questionsLoaded: false, // Flag to prevent multiple calls
    }
  },
  computed: {
    resolvedInitialQuestion() {
      if (!this.initialQuestion) return null
      
      // If it's already an object, return it
      if (typeof this.initialQuestion === 'object') {
        return this.initialQuestion
      }
      
      // If it's an ID, find the question from questions list
      if (this.questions && this.questions.length > 0) {
        return this.questions.find(q => q.id == this.initialQuestion) || null
      }
      
      return null
    },
  },

  watch: {
    surveyInfo: {
      handler(newVal) {
        if (newVal && !this.questionsLoaded) {
          this.fetchQuestions()
        }
      },
      immediate: false,
    },
    surveyId: {
      handler(newVal) {
        if (newVal && !this.questionsLoaded) {
          this.fetchQuestions()
        }
      },
      immediate: false,
    },
    resolvedInitialQuestion: {
      handler(newVal) {
        if (newVal) {
          this.selected = newVal
        } else {
          this.selected = null
        }
      },
      deep: true,
      immediate: true,
    },
    questions: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.initialQuestion) {
          // Questions loaded, now set the selected question
          if (typeof this.initialQuestion === 'number' || typeof this.initialQuestion === 'string') {
            this.selected = newVal.find(q => q.id == this.initialQuestion) || null
          } else if (typeof this.initialQuestion === 'object') {
            this.selected = this.initialQuestion
          }
        }
      },
      deep: true,
    },
    selected: {
      handler(newVal) {
        this.$emit('selectionChanged', newVal)
      },
      deep: true,
    },
  },

  mounted() {
    // Fetch questions when component is mounted if surveyId or surveyInfo is available
    const surveyId = this.surveyId || (this.surveyInfo && this.surveyInfo.id)
    if (surveyId && !this.questionsLoaded) {
      this.fetchQuestions()
    }
  },

  methods: {
    fetchQuestions() {
      const surveyId = this.surveyId || (this.surveyInfo && this.surveyInfo.id) || null
      
      if (!surveyId) {
        return
      }

      const USER_APP_STORE_MODULE_NAME = "app-survey-questions"
      
      // Register module if not already registered
      if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) {
        store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)
      }

      // Prevent multiple calls
      if (this.questionsLoaded) {
        return Promise.resolve(this.questions || [])
      }

      return new Promise((resolve, reject) => {
        this.questionsLoaded = true // Set flag before making the call
        store
          .dispatch('app-survey-questions/fetchUnlinkedQuestions', {
            id: surveyId,
          })
          .then((response) => {
            const data = response.data
            const responseData = JSON.parse(JSON.stringify(data))
            this.questions = responseData.data || []
            resolve(this.questions)
          })
          .catch((error) => {
            console.error('Error fetching unlinked questions:', error)
            this.questionsLoaded = false // Reset flag on error so it can retry
            reject(error)
          })
      })
    },
    onSelectionChanged(question) {
      this.$emit('selectionChanged', question)
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.question-picker-wrapper {
  width: 100%;
}

.question-picker-select {
  width: 100%;
  
  .vs__dropdown-toggle {
    min-height: 38px;
    padding: 4px 12px;
  }
  
  .vs__selected {
    font-size: 0.9rem;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .question-option {
    padding: 8px 0;
    min-height: 40px;
    
    span {
      line-height: 1.5;
      word-break: break-word;
    }
  }
}

.question-picker-inline {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  .question-picker-select {
    width: 100%;
    max-width: 100%;
    
    .vs__dropdown-toggle {
      min-height: 32px;
      padding: 2px 8px;
      max-width: 100%;
      overflow: hidden;
      display: flex;
      align-items: center;
      position: relative;
    }
    
    .vs__selected {
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
      max-width: calc(100% - 50px);
      position: relative;
      
      .selected-option-content {
        max-width: 100%;
        overflow: hidden;
        display: block;
        position: relative;
        
        span {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
          word-break: break-word;
          
          /* Remove HTML tags for text truncation */
          * {
            display: inline;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
    
    .vs__selected-option {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: calc(100% - 50px);
      flex: 1;
      min-width: 0;
      
      .selected-option-content {
        max-width: 100%;
        overflow: hidden;
        display: block;
        
        span {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
          word-break: break-word;
          
          /* Remove HTML tags for text truncation */
          * {
            display: inline;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }
    
    .vs__actions {
      flex-shrink: 0;
      padding-left: 4px;
      margin-left: auto;
      min-width: 40px;
    }
    
    .vs__clear {
      flex-shrink: 0;
    }
    
    .vs__search {
      max-width: 100%;
    }
    
    .vs__dropdown-menu {
      max-width: 100%;
      overflow-x: hidden;
    }
  }
}
</style>

